import {
  AvailableSchedulePeriod,
  ITimeSlotTimestamp,
} from '../models/schedule-calendar.models';

/** Epoch milliseconds, as timestamps from the API may vary in format */
interface IRange {
  start: number;
  end: number;
}

type Period = IRange & { isPreferred: boolean };

const toPeriod = ({ start, end, isPreferred }: AvailableSchedulePeriod) => ({
  start: Date.parse(start),
  end: Date.parse(end),
  isPreferred,
});

const toRange = ({ start, end }: ITimeSlotTimestamp): IRange => ({
  start: Date.parse(start),
  end: Date.parse(end),
});

const toSchedulePeriod = ({
  start,
  end,
  isPreferred,
}: Period): AvailableSchedulePeriod => ({
  start: new Date(start).toISOString(),
  end: new Date(end).toISOString(),
  isPreferred,
});

const byStart = (a: IRange, b: IRange) => a.start - b.start;

/** Whether the ranges overlap or touch */
const meets = (a: IRange, b: IRange) => a.start <= b.end && b.start <= a.end;

/** Remove a set of times from a period that will either be replaced or
 * removed entirely.
 */
const removeTimesFromPeriod = (period: Period, times: IRange[]) =>
  times.reduce(
    (periods, time) =>
      periods.flatMap((period) => {
        if (time.end <= period.start || time.start >= period.end) {
          return [period];
        }

        return [
          ...(period.start < time.start
            ? [{ ...period, end: time.start }]
            : []),
          ...(period.end > time.end ? [{ ...period, start: time.end }] : []),
        ];
      }),
    [period]
  );

/** Joins periods that touch or overlap with the same preference */
const joinPeriods = (periods: Period[]) =>
  [...periods].sort(byStart).reduce<Period[]>((joined, period) => {
    const last = joined.at(-1);

    if (last?.isPreferred === period.isPreferred && period.start <= last.end) {
      return [
        ...joined.slice(0, -1),
        { ...last, end: Math.max(last.end, period.end) },
      ];
    }

    return [...joined, period];
  }, []);

/**
 * Merges slots that touch or overlap into the continuous blocks of time they
 * cover. Only the start and end are kept, so any other properties, such as
 * preference, are dropped - group slots by them before merging.
 */
export const mergeContiguous = (
  slots: ITimeSlotTimestamp[]
): ITimeSlotTimestamp[] =>
  slots
    .map(toRange)
    .sort(byStart)
    .reduce<IRange[]>((merged, range) => {
      const last = merged.at(-1);

      if (last && range.start <= last.end) {
        return [
          ...merged.slice(0, -1),
          { ...last, end: Math.max(last.end, range.end) },
        ];
      }

      return [...merged, range];
    }, [])
    .map(({ start, end }) => ({
      start: new Date(start).toISOString(),
      end: new Date(end).toISOString(),
    }));

const periodIsChanged = (period: Period, times: IRange[]) =>
  times.some((range) => meets(period, range));

/**
 * Removes time from the existing periods, then adds periods to them. Where
 * they overlap, the added periods replace the existing, so whether they're
 * preferred takes precedence. Existing periods not meeting the added or
 * removed time are left as they are.
 *
 * Adding new times removes nothing, while editing removes the times being
 * edited, so whatever the edit no longer covers is gone.
 */
export const mergeAvailableSchedulePeriods = (
  existing: AvailableSchedulePeriod[],
  added: AvailableSchedulePeriod[],
  removed: ITimeSlotTimestamp[] = []
): AvailableSchedulePeriod[] => {
  const adding = added.map(toPeriod);
  const removing = removed.map(toRange);
  const replacing = [...adding, ...removing];

  const existingPeriods = existing.map(toPeriod);

  const merged = joinPeriods([
    ...existingPeriods.flatMap((period) =>
      periodIsChanged(period, replacing)
        ? removeTimesFromPeriod(period, replacing)
        : []
    ),
    ...adding,
  ]);

  return [
    ...existingPeriods.filter((period) => !periodIsChanged(period, replacing)),
    ...merged,
  ]
    .sort(byStart)
    .map(toSchedulePeriod);
};

/**
 * Whether the periods differ from the initial ones, in order, by their times
 * or preference. Timestamps are compared as instants, so the same time in a
 * different format is unchanged.
 *
 * Periods must be merged prior to using this function. Using on unmerged schedule
 * periods will provide a false result.
 */
export const haveAvailableSchedulePeriodsChanged = (
  initial: AvailableSchedulePeriod[],
  current: AvailableSchedulePeriod[]
) => {
  if (initial.length !== current.length) {
    return true;
  }

  return initial.some((period, i) => {
    const other = current[i];

    return (
      Date.parse(period.start) !== Date.parse(other.start) ||
      Date.parse(period.end) !== Date.parse(other.end) ||
      period.isPreferred !== other.isPreferred
    );
  });
};
